import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message as toast } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { SupportTemplate } from 'pages/support-shared/types';
import { renderTemplate, supportError, useSupportMeta } from 'pages/support-shared/utils';
import { TABLE_EMPTY } from 'pages/star-shared/utils';

interface FormValue {
  title: string;
  shortcut?: string;
  category?: string;
  content: string;
  isActive: boolean;
}

const SAMPLE_VARS: Record<string, string> = {
  ten: 'Lan',
  ho_ten: 'Nguyễn Thị Lan',
  username: 'lan.nguyen',
  nhan_vien: 'Minh',
  ngay: '28/09/2026',
  gio_truc: '08:00–22:00, T2–CN',
};

/** Văn mẫu: ADMIN và MOD cùng thêm/sửa. Không xoá — tắt để ẩn (giữ thống kê lượt dùng). */
const TemplatesTab = () => {
  const meta = useSupportMeta();
  const [rows, setRows] = useState<SupportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string | undefined>();
  const [editing, setEditing] = useState<SupportTemplate | 'new' | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<FormValue>();
  const content = Form.useWatch('content', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await AxiosUtils.get(API.supportTemplates, { scope: 'all', q: q || undefined, category });
      setRows(res.data || []);
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setLoading(false);
    }
  }, [q, category]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (row: SupportTemplate | 'new') => {
    setEditing(row);
    form.setFieldsValue(
      row === 'new'
        ? { title: '', shortcut: '', category: undefined, content: '', isActive: true }
        : {
            title: row.title,
            shortcut: row.shortcut || '',
            category: row.category || undefined,
            content: row.content,
            isActive: row.isActive,
          }
    );
  };

  const save = async () => {
    const value = await form.validateFields();
    setSaving(true);
    const body = {
      title: value.title,
      shortcut: value.shortcut?.trim() || null,
      category: value.category || null,
      content: value.content,
      isActive: value.isActive,
    };
    try {
      if (editing === 'new') await AxiosUtils.post(API.supportTemplates, body);
      else if (editing) await AxiosUtils.patch(`${API.supportTemplates}/${editing.id}`, body);
      toast.success('Đã lưu văn mẫu.');
      setEditing(null);
      load();
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: SupportTemplate, isActive: boolean) => {
    try {
      await AxiosUtils.patch(`${API.supportTemplates}/${row.id}`, { isActive });
      load();
    } catch (error) {
      toast.error(supportError(error));
    }
  };

  const categoryLabel = (value: string | null) => meta?.categories.find((c) => c.value === value)?.label || '—';

  const columns: ColumnsType<SupportTemplate> = [
    { title: 'Tên', dataIndex: 'title', key: 'title', width: 220 },
    {
      title: 'Lối tắt',
      key: 'shortcut',
      width: 130,
      render: (_, r) => (r.shortcut ? <Tag>/{r.shortcut}</Tag> : '—'),
    },
    { title: 'Loại vấn đề', key: 'category', width: 170, render: (_, r) => categoryLabel(r.category) },
    {
      title: 'Nội dung',
      key: 'content',
      render: (_, r) => (
        <div style={{ whiteSpace: 'pre-wrap', maxWidth: 520, maxHeight: 88, overflow: 'hidden' }}>{r.content}</div>
      ),
    },
    { title: 'Lượt dùng', dataIndex: 'usageCount', key: 'usageCount', width: 100, align: 'right' },
    {
      title: 'Bật',
      key: 'isActive',
      width: 80,
      render: (_, r) => <Switch size="small" checked={r.isActive} onChange={(v) => toggle(r, v)} />,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => open(r)}>
          Sửa
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageTitle
        title="Văn mẫu"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            Thêm văn mẫu
          </Button>
        }
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Tìm tên, lối tắt, nội dung"
          style={{ width: 280 }}
          onPressEnter={(e) => setQ((e.target as HTMLInputElement).value.trim())}
          onChange={(e) => !e.target.value && setQ('')}
        />
        <Select
          allowClear
          placeholder="Loại vấn đề"
          style={{ width: 220 }}
          value={category}
          onChange={setCategory}
          options={meta?.categories || []}
        />
      </Space>
      <div className="custom-table">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={TABLE_EMPTY}
          scroll={{ x: 1100 }}
        />
      </div>

      <Modal
        open={!!editing}
        title={editing === 'new' ? 'Thêm văn mẫu' : 'Sửa văn mẫu'}
        okText="Lưu"
        cancelText="Huỷ"
        onOk={save}
        onCancel={() => setEditing(null)}
        confirmLoading={saving}
        width={680}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="title" label="Tên văn mẫu" rules={[{ required: true, whitespace: true, message: 'Nhập tên văn mẫu.' }]}>
            <Input maxLength={120} placeholder="vd. Hướng dẫn chấm công bằng WiFi" />
          </Form.Item>
          <Space size={16} style={{ display: 'flex' }} align="start">
            <Form.Item
              name="shortcut"
              label="Lối tắt"
              extra="Gõ /lối-tắt trong ô trả lời. Không dấu, không khoảng trắng."
              style={{ flex: 1 }}
            >
              <Input maxLength={40} prefix="/" placeholder="chamcong" />
            </Form.Item>
            <Form.Item name="category" label="Loại vấn đề" style={{ flex: 1 }}>
              <Select allowClear options={meta?.categories || []} placeholder="Chưa phân loại" />
            </Form.Item>
          </Space>
          <Form.Item
            name="content"
            label="Nội dung"
            rules={[{ required: true, whitespace: true, message: 'Nhập nội dung.' }]}
            extra={
              <span>
                Biến tự thay khi chèn:{' '}
                {(meta?.templateVariables || []).map((v) => (
                  <Tag key={v.key} title={v.label} style={{ marginBottom: 4 }}>
                    {`{${v.key}}`}
                  </Tag>
                ))}
              </span>
            }
          >
            <Input.TextArea autoSize={{ minRows: 5, maxRows: 14 }} maxLength={4000} />
          </Form.Item>
          {!!content?.trim() && (
            <div
              style={{
                background: '#F6F0F3',
                borderRadius: 12,
                padding: '8px 12px',
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 12, color: '#9A8F9E', marginBottom: 4 }}>Xem trước (dữ liệu mẫu):</div>
              {renderTemplate(content, SAMPLE_VARS)}
            </div>
          )}
          <Form.Item name="isActive" label="Đang dùng" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TemplatesTab;
